import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { capitalizeFirstLetter, getChapterHighlightStyle } from "./stockUtils";
import {
    fetchStockDataWithFallback,
    getRecentDateStrings,
    fetchChapterHighlights,
} from "./dataFetcher";

import "./body.css";
import "./fave.css";

function Search() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [stocks, setStocks] = useState([]);
    const [results, setResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState("");
    const [highlightMap, setHighlightMap] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Invest With Knowledge! - Search";

        fetchStockDataWithFallback(getRecentDateStrings(4))
            .then(async ({ combined, dateStr }) => {
                setStocks(combined);
                const tickers = Array.from(
                    new Set(
                        combined
                            .map((stock) => (stock.ticker || "").toLowerCase())
                            .filter((ticker) => ticker.length > 0)
                    )
                );

                if (tickers.length && dateStr) {
                    try {
                        const highlights = await fetchChapterHighlights(dateStr, tickers);
                        setHighlightMap(highlights);
                    } catch (highlightError) {
                        console.warn("Unable to fetch highlight data for search:", highlightError);
                        setHighlightMap({});
                    }
                } else {
                    setHighlightMap({});
                }

                setIsReady(true);
            })
            .catch((fetchError) => {
                console.error("There was a problem with the fetch operation:", fetchError);
                setError("Unable to load stocks right now. Please try again later.");
                setHighlightMap({});
            });
    }, []);

    const handleSubmit = (event) => {
        event.preventDefault();
        const term = query.trim().toLowerCase();
        if (!term) {
            setResults([]);
            setHasSearched(false);
            return;
        }

        const scored = [];
        for (const stock of stocks) {
            const ticker = (stock.ticker || "").toLowerCase();
            const name = (stock.name || "").toLowerCase();

            let score = 0;
            if (ticker === term || name === term) score = 4;
            else if (ticker.startsWith(term)) score = 3;
            else if (name.startsWith(term)) score = 2;
            else if (ticker.includes(term) || name.includes(term)) score = 1;

            if (score > 0) scored.push({ stock, score });
        }
        scored.sort((a, b) => b.score - a.score);
        const filtered = scored.map((r) => r.stock);

        setResults(filtered);
        setHasSearched(true);
    };

    const handleSelect = (stock) => {
        navigate(`/business/${(stock.ticker || "").toLowerCase()}`);
    };

    const shouldAnimateResults = isReady && hasSearched;
    const resultClass = `stockdiv${shouldAnimateResults ? " fade-in" : ""}`;

    return (
        <section className="mainbody">
            <h2 className="responsiveheader">Search</h2>

            <form className="archivediv" onSubmit={handleSubmit}>
                <p className="centeredp">Look up a stock</p>
                <p>
                    <label htmlFor="stock-search">
                        Stock name or ticker:
                    </label>
                </p>
                <p>
                    <input
                        id="stock-search"
                        type="text"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setHasSearched(false);
                        }}
                        placeholder="e.g. Apple or AAPL"
                    />
                </p>
                <p className="centeredp">
                    <button type="submit">Submit</button>
                </p>
                {error ? <p className="centeredp">{error}</p> : null}
            </form>

            <div>
                {!isReady && !error ? (
                    <p className="centeredp">Loading stocks...</p>
                ) : null}
                {isReady && !hasSearched && query.trim() === "" ? (
                    <p className="centeredp">Enter a stock name or ticker to begin.</p>
                ) : null}

                {hasSearched ? (
                    <>
                        <p className="centeredp">Search Results</p>
                        {isReady && results.length === 0 ? (
                            <p className="centeredp">No matches found.</p>
                        ) : null}
                        {results.map((stock, index) => {
                            const delayStyle = shouldAnimateResults
                                ? { animationDelay: `${index * 120}ms` }
                                : undefined;
                            const tickerKey = (stock.ticker || "").toLowerCase();
                            const highlight = highlightMap[tickerKey];
                            const highlightStyle = getChapterHighlightStyle(highlight);

                            const cardStyle =
                                delayStyle || highlightStyle
                                    ? { ...(highlightStyle || {}), ...(delayStyle || {}) }
                                    : undefined;

                            return (
                                <div
                                    className={resultClass}
                                    key={`${stock.ticker}-${index}`}
                                    style={cardStyle}
                                >
                                    <p>Name: {stock.name}</p>
                                    <p>Industry: {capitalizeFirstLetter(stock.industry || "")}</p>
                                    <p>Ticker: {stock.ticker}</p>
                                    <p className="centeredp">
                                        <button type="button" onClick={() => handleSelect(stock)}>
                                            View More Details
                                        </button>
                                    </p>
                                </div>
                            );
                        })}
                    </>
                ) : null}
            </div>
        </section>
    );
}

export default Search;
